import { getCountryRules, getSupportedCountries } from "@/lib/country/registry";
import { comparisonsMap } from "@/data/comparisons";
import { slugify } from "@/lib/utils/slugify";

export interface GlossaryTermRef {
  term: string;
  slug: string;
  country: string;
  /** The term's real one-line definition, used as the link description. */
  definition: string;
}

function getSlugFromUrl(url: string): string {
  return url.split("/").pop() || "";
}

type ReverseMap = Record<string, GlossaryTermRef[]>;

let _calcMap: ReverseMap | null = null;
let _compMap: ReverseMap | null = null;

function buildMaps(): { calcMap: ReverseMap; compMap: ReverseMap } {
  if (_calcMap && _compMap) return { calcMap: _calcMap, compMap: _compMap };

  const calcMap: ReverseMap = {};
  const compMap: ReverseMap = {};

  const countries = getSupportedCountries();
  for (const country of countries) {
    const rules = getCountryRules(country);
    const glossary = rules.glossary || [];

    for (const entry of glossary) {
      const slug = slugify(entry.term);
      const ref: GlossaryTermRef = { term: entry.term, slug, country: country.toLowerCase(), definition: entry.definition };

      for (const toolUrl of entry.relatedTools || []) {
        if (toolUrl.startsWith("/compare/")) {
          const compSlug = toolUrl.replace("/compare/", "");
          if (!compMap[compSlug]) compMap[compSlug] = [];
          if (!compMap[compSlug].some(r => r.term === ref.term && r.country === ref.country)) {
            compMap[compSlug].push(ref);
          }
        } else {
          const calcSlug = getSlugFromUrl(toolUrl);
          if (calcSlug) {
            if (!calcMap[calcSlug]) calcMap[calcSlug] = [];
            if (!calcMap[calcSlug].some(r => r.term === ref.term && r.country === ref.country)) {
              calcMap[calcSlug].push(ref);
            }
          }
        }
      }
    }
  }

  _calcMap = calcMap;
  _compMap = compMap;
  return { calcMap, compMap };
}

export function getGlossaryTermsForCalculator(calculatorSlug: string, country: string): GlossaryTermRef[] {
  const { calcMap } = buildMaps();
  const matches = calcMap[calculatorSlug] || [];
  return matches.filter(m => m.country === country.toLowerCase());
}

export function getGlossaryTermsForComparison(comparisonSlug: string, country: string): GlossaryTermRef[] {
  const { compMap } = buildMaps();
  const matches = compMap[comparisonSlug] || [];
  return matches.filter(m => m.country === country.toLowerCase());
}

